import { Box, Button, Heading, Spinner, Text } from '@grupoboticario/flora-react'
import { useFormik } from 'formik'
import { Dispatch, SetStateAction, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import { SchemaOf } from 'yup'
import { ExclamationMarkTriangleFillIcon } from '@grupoboticario/flora-react-icons'
import { CheckCircleIcon } from '@grupoboticario/flora-react-icons'

import { Dispatcher } from '../config/schemaDispatcher'
import { Driver } from '../config/schemaDriver'
import useGeolocation from '../hooks/useGeolocation'
import http from '../config/http'

export type TProps = {
  uuid: string
  schema: SchemaOf<Driver | Dispatcher>
  title: string
  type: string
  isSubmited: boolean
  onSetIsSubmited: Dispatch<SetStateAction<boolean>>
}

const withForm = <P extends object>(Component: React.ComponentType<P>) => {
  const FormComponent = ({ uuid, schema, title, type, isSubmited, onSetIsSubmited }: TProps) => {
    const { t } = useTranslation()
    const [saving, setSaving] = useState(false)
    const position = useGeolocation()
    const { handleSubmit, resetForm, ...formik } = useFormik({
      initialValues: schema.getDefault(),
      validationSchema: schema,
      onSubmit: (val) => {
        setSaving(true)
        http
          .patch(`/order-ops/romaneio/sign/${uuid}`, { ...val, ...position })
          .then(() => {
            Swal.fire({ title: t('successTitle'), text: t('successText'), icon: 'success' })
            resetForm()
            onSetIsSubmited(true)
          })
          .catch((err) => {
            Swal.fire({ title: t('errorTitle'), text: `${err.response?.data || t('errorTitle')}`, icon: 'error' })
          })
          .finally(() => setSaving(false))
      }
    })

    if (isSubmited)
      return (
        <Box
          css={{
            '@tablet': {
              width: 600
            },
            margin: '80px auto 50px',
            width: '100%',
            gap: '$2',
            display: 'flex'
          }}
        >
          <Box css={{ marginRight: '16px', color: '#0CC841' }}>
            <CheckCircleIcon size={80} />
          </Box>
          <Heading level={5} as="h2" css={{ display: 'block', marginTop: '20px' }}>
            {t('formMsgSuccess', { type: t(type) })}
          </Heading>
        </Box>
      )

    return (
      <Box
        css={{
          '@tablet': {
            width: 600
          },
          margin: '0 auto',
          width: '100%',
          gap: '$2'
        }}
      >
        <form onSubmit={handleSubmit}>
          <Heading level={5} as="h2" css={{ display: 'block', marginTop: '20px', fontSize: '24px', fontWeight: '500' }}>
            {t(title)}
          </Heading>
          <Text css={{ display: 'block', margin: '15px 0', fontSize: '12px', fontWeight: '400' }}>{t('formMsg')}</Text>

          <Component {...(formik as P)} />

          <Box
            css={{
              display: 'flex',
              margin: '16px 0',
              border: '1px solid #ccc',
              padding: '12px 16px',
              borderRadius: '10px'
            }}
          >
            <Box css={{ marginRight: '8px', color: '#FF9700' }}>
              <ExclamationMarkTriangleFillIcon />
            </Box>

            <Text className="statusCapitalized" css={{ display: 'inline-block', fontSize: '13px', lineHeight: '24px' }}>
              <Trans
                i18nKey="formDisclaimer"
                components={{ strong: <strong /> }}
                values={{ term: t(type === 'dispatcher' ? 'dispatched' : 'collected') }}
              />
            </Text>
          </Box>

          <Box>
            <Button type="submit" disabled={saving} data-testid="submit" css={{ fontWeight: 'bold' }}>
              {saving && (
                <>
                  <Spinner size="xs" speed="800ms" thickness="2px" />
                  <Box css={{ marginLeft: '8px', display: 'inline-block' }}>{t('saving')}</Box>
                </>
              )}
              {!saving && t('sign')}
            </Button>

            <Button
              variant="ghost"
              type="button"
              disabled={saving}
              onClick={() => resetForm()}
              css={{ fontWeight: 'bold', marginLeft: '8px' }}
            >
              {t('erase')}
            </Button>
          </Box>
        </form>
      </Box>
    )
  }

  FormComponent.displayName = 'FormComponent'

  return FormComponent
}

export default withForm
